/* eslint-disable camelcase */
import { getBackVersion } from '../../config';
import { RentalFileSnapshot } from '../../models/RentalPayment/Files/File';
import { RentalFileWithNoteSnapshot } from '../../models/RentalPayment/Files/FileWithNote';
import api from '../api';
import { UploadSeed } from '../upload';

type SeedCreation = {
    contentMd5: string;
    mimeType: string;
    fileSize: number;
    originalFileName: string;
};

type Seed = SeedCreation & {
    id: string;
    workspaceId: string;
    authorAccountId: number;
    s3Path: string;
    signedUrl: UploadSeed;
};

type DownloadUrl = {
    signedUrl: string;
};

type RawSeed = {
    content_md5: string;
    mime_type: string;
    file_size: number;
    original_file_name: string;
    id: string;
    workspace_id: string;
    author_account_id: number;
    s3_path: string;
    signed_url: UploadSeed;
};

const getContractFiles = (contractId: string): Promise<RentalFileSnapshot[]> =>
    api.request<RentalFileSnapshot[]>({
        method: 'GET',
        url: `${getBackVersion()}/files`,
        params: {
            attached_to: 'rental_contract',
            attached_to_id: contractId,
        },
    });

const getContractFile = (
    fileSeedId: string
): Promise<RentalFileWithNoteSnapshot> =>
    api.request<RentalFileWithNoteSnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/files/${fileSeedId}`,
    });

const createFileSeed = async (data: SeedCreation): Promise<Seed> => {
    const result = await api.request<RawSeed>({
        method: 'POST',
        url: `${getBackVersion()}/files/seed`,
        data: {
            content_md5: data.contentMd5,
            mime_type: data.mimeType,
            file_size: data.fileSize,
            original_file_name: data.originalFileName,
        },
        skipCamelization: true,
    });

    return {
        contentMd5: result.content_md5,
        mimeType: result.mime_type,
        fileSize: result.file_size,
        originalFileName: result.original_file_name,
        id: result.id,
        workspaceId: result.workspace_id,
        authorAccountId: result.author_account_id,
        s3Path: result.s3_path,
        signedUrl: result.signed_url,
    };
};

const saveFile = (
    seedId: string,
    fileName: string,
    attachedToId: string
): Promise<RentalFileSnapshot> =>
    api.request<RentalFileSnapshot>({
        method: 'POST',
        url: `${getBackVersion()}/files/${seedId}`,
        data: {
            file_name: fileName,
            attached_to: 'rental_contract',
            attached_to_id: attachedToId,
        },
    });

const downloadFile = (seedId: string): Promise<DownloadUrl> =>
    api.request<DownloadUrl>({
        method: 'GET',
        url: `${getBackVersion()}/files/${seedId}/download`,
    });

const removeFile = (seedId: string): Promise<void> =>
    api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/files/${seedId}`,
    });

const patchFilename = (seedId: string, filename: string): Promise<void> =>
    api.request({
        method: 'PATCH',
        url: `${getBackVersion()}/files/${seedId}`,
        data: {
            file_name: filename,
        },
    });

const patchNotes = (seedId: string, notes: string): Promise<void> =>
    api.request({
        method: 'PATCH',
        url: `${getBackVersion()}/files/${seedId}/notes`,
        data: {
            content: notes,
        },
    });

export {
    getContractFiles,
    getContractFile,
    createFileSeed,
    saveFile,
    downloadFile,
    removeFile,
    patchFilename,
    patchNotes,
};
